import type {
  TimeOffPolicyDto,
  TimeOffTypeDto,
  SeniorityMatrixEntryDto,
  HolidayDto,
  TimeOffRequestDto,
  TimeOffBalanceDto,
  TimeOffSummaryDto,
  AbsenceDayDto,
  TimeOffAbsenceSummaryDto,
  BulkImportHistoricalResultDto,
} from '../types';

export const timeOffPolicyMock: TimeOffPolicyDto = {
  approvalLevel: 'SingleApprover',
  responseLimit: 3,
  responseLimitUnit: 'BusinessDays',
  onExhausted: 'AutoApprove',
  minAdvanceBusinessDays: 2,
  resolutionMarginBusinessDays: 1,
  graceToleranceDays: 0,
  carryOverMode: 'Accumulate',
  allowAdvance: true,
  maxAdvanceDays: 5,
};

export const timeOffTypesMock: TimeOffTypeDto[] = [
  {
    id: 'type-vacation',
    name: 'Vacaciones',
    code: 'vacation',
    consumesVacationBalance: true,
    requiresApproval: true,
    allowsRetroactive: false,
    allowsHourly: false,
    requiresAttachment: false,
    isSystem: true,
    isActive: true,
    order: 1,
  },
  {
    id: 'type-sick',
    name: 'Incapacidad',
    code: 'sick',
    consumesVacationBalance: false,
    requiresApproval: true,
    allowsRetroactive: true,
    allowsHourly: false,
    requiresAttachment: true,
    isSystem: true,
    isActive: true,
    order: 2,
  },
  {
    id: 'type-personal',
    name: 'Permiso personal',
    code: 'personal',
    consumesVacationBalance: false,
    requiresApproval: true,
    allowsRetroactive: false,
    allowsHourly: true,
    requiresAttachment: false,
    isSystem: false,
    isActive: true,
    order: 3,
  },
  {
    id: 'type-maternity',
    name: 'Maternidad',
    code: 'maternity',
    consumesVacationBalance: false,
    requiresApproval: true,
    allowsRetroactive: false,
    allowsHourly: false,
    requiresAttachment: true,
    isSystem: false,
    isActive: false,
    order: 4,
  },
];

export const seniorityMatrixMock: SeniorityMatrixEntryDto[] = [
  { fromYear: 0, toYear: 1, days: 12 },
  { fromYear: 1, toYear: 5, days: 15 },
  { fromYear: 5, toYear: null, days: 20 },
];

export const holidaysMock: HolidayDto[] = [
  { id: 'holiday-1', date: '2026-01-01', name: 'Año Nuevo', isOfficial: true },
  { id: 'holiday-2', date: '2026-05-01', name: 'Día del Trabajo', isOfficial: true },
  { id: 'holiday-3', date: '2026-09-16', name: 'Día de la Independencia', isOfficial: true },
  { id: 'holiday-4', date: '2026-12-25', name: 'Navidad', isOfficial: true },
  { id: 'holiday-5', date: '2026-07-20', name: 'Día del fundador', isOfficial: false },
];

/** A pending request with a two-step chain — the first step is active, the second still `Waiting`. */
export const timeOffRequestPendingMock: TimeOffRequestDto = {
  id: 'request-1',
  tenantId: 'mock-tenant',
  requesterUserId: 'user-1',
  typeId: 'type-vacation',
  reason: 'Viaje familiar',
  cycleId: 'cycle-1',
  advancedDays: 0,
  chainDeadlineAt: '2026-08-10T00:00:00Z',
  submittedAt: '2026-08-03T10:00:00Z',
  status: 'Pending',
  resolutionMode: null,
  resolvedAt: null,
  resolvedByUserId: null,
  days: [
    {
      id: 'day-1',
      date: '2026-08-20',
      startTime: null,
      endTime: null,
      isFullDay: true,
      status: 'Active',
    },
    {
      id: 'day-2',
      date: '2026-08-21',
      startTime: null,
      endTime: null,
      isFullDay: true,
      status: 'Active',
    },
  ],
  steps: [
    {
      id: 'step-1',
      order: 0,
      approverUserId: 'user-2',
      source: 'DirectManager',
      status: 'Pending',
      deadlineAt: '2026-08-10T00:00:00Z',
    },
    {
      id: 'step-2',
      order: 1,
      approverUserId: 'user-3',
      source: 'AreaLeader',
      status: 'Waiting',
      deadlineAt: null,
    },
  ],
  attachments: [],
};

/** An approved request, resolved manually by the second approver. */
export const timeOffRequestApprovedMock: TimeOffRequestDto = {
  id: 'request-2',
  tenantId: 'mock-tenant',
  requesterUserId: 'user-1',
  typeId: 'type-sick',
  reason: 'Cita médica',
  cycleId: 'cycle-1',
  advancedDays: 0,
  chainDeadlineAt: null,
  submittedAt: '2026-07-15T09:00:00Z',
  status: 'Approved',
  resolutionMode: 'Manual',
  resolvedAt: '2026-07-16T14:00:00Z',
  resolvedByUserId: 'user-2',
  days: [
    {
      id: 'day-3',
      date: '2026-07-18',
      startTime: null,
      endTime: null,
      isFullDay: true,
      status: 'Active',
    },
  ],
  steps: [
    {
      id: 'step-3',
      order: 0,
      approverUserId: 'user-2',
      source: 'DirectManager',
      status: 'Approved',
      deadlineAt: '2026-07-18T00:00:00Z',
    },
  ],
  attachments: [
    {
      id: 'attachment-1',
      fileName: 'constancia-medica.pdf',
      contentType: 'application/pdf',
      sizeBytes: 245_760,
      uploadedById: 'user-1',
      uploadedAt: '2026-07-15T09:05:00Z',
    },
  ],
};

/** A peer's request as seen at `TypeAndDatesOnly` visibility — reason/attachments already stripped. */
export const timeOffRequestRedactedMock: TimeOffRequestDto = {
  id: 'request-3',
  tenantId: 'mock-tenant',
  requesterUserId: 'user-3',
  typeId: 'type-vacation',
  reason: null,
  cycleId: 'cycle-2',
  advancedDays: 0,
  chainDeadlineAt: null,
  submittedAt: '2026-08-01T09:00:00Z',
  status: 'Approved',
  resolutionMode: 'Timeout',
  resolvedAt: '2026-08-05T00:00:00Z',
  resolvedByUserId: null,
  days: [
    {
      id: 'day-4',
      date: '2026-08-25',
      startTime: null,
      endTime: null,
      isFullDay: true,
      status: 'Active',
    },
  ],
  steps: [
    {
      id: 'step-4',
      order: 0,
      approverUserId: 'user-2',
      source: 'DirectManager',
      status: 'Approved',
      deadlineAt: '2026-08-05T00:00:00Z',
    },
  ],
  attachments: [],
};

export const myTimeOffRequestsMock: TimeOffRequestDto[] = [
  timeOffRequestPendingMock,
  timeOffRequestApprovedMock,
];

export const pendingTimeOffApprovalsMock: TimeOffRequestDto[] = [timeOffRequestPendingMock];

export const teamTimeOffAbsencesMock: TimeOffRequestDto[] = [
  timeOffRequestPendingMock,
  timeOffRequestRedactedMock,
];

export const timeOffBalanceMock: TimeOffBalanceDto = {
  cycleId: 'cycle-1',
  cycleNumber: 2,
  startDate: '2026-01-01',
  endDate: '2026-12-31',
  entitledDays: 15,
  carriedOverDays: 3,
  advancedOutDays: 0,
  usedDays: 4,
  reservedDays: 2,
  adjustmentDays: 1,
  availableDays: 13,
};

export const timeOffSummaryMock: TimeOffSummaryDto = {
  availableDays: 13,
  entitledDays: 15,
  usedDays: 4,
  reservedDays: 2,
  cycleStartDate: '2026-01-01',
  cycleEndDate: '2026-12-31',
  nextAbsenceDate: '2026-08-20',
  nextAbsenceTypeName: 'Vacaciones',
  pendingRequestCount: 1,
};

export const adminTodayMock: AbsenceDayDto[] = [
  {
    requestId: 'request-4',
    userId: 'user-4',
    typeId: 'type-vacation',
    typeName: 'Vacaciones',
    date: '2026-08-06',
    isFullDay: true,
  },
];

export const adminUpcomingMock: AbsenceDayDto[] = [
  {
    requestId: 'request-1',
    userId: 'user-1',
    typeId: 'type-vacation',
    typeName: 'Vacaciones',
    date: '2026-08-20',
    isFullDay: true,
  },
  {
    requestId: 'request-5',
    userId: 'user-5',
    typeId: 'type-sick',
    typeName: 'Incapacidad',
    date: '2026-08-12',
    isFullDay: true,
  },
];

export const adminSummaryMock: TimeOffAbsenceSummaryDto = {
  totalRequests: 12,
  totalAbsenceDays: 34,
  daysByType: { Vacaciones: 20, Incapacidad: 10, 'Permiso personal': 4 },
};

export const timeOffAttachmentBlobMock = new Blob(['mock-attachment-content'], {
  type: 'application/pdf',
});

/** A bulk historical-import result — 2 requests created from the file, 1 row failed. */
export const bulkImportHistoricalResultMock: BulkImportHistoricalResultDto = {
  created: ['historical-request-1', 'historical-request-2'],
  errors: [{ rowIndex: 3, errorCode: 'TimeOff.OverlappingRequest' }],
};
